import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Calendar from 'react-calendar';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import AddToCalendarModal from './AddToCalendarModal/AddToCalendarModal';
import CenteredModal from '../ui/CenteredModal';
import AssignmentDetails from '../assignments/AssignmentDetails';
import EventDetails from '../events/EventDetails';
import 'react-calendar/dist/Calendar.css';
import './Calendar.css';
import '../primitives/Button.css';
import CalendarDayIcon from './CalendarDayIcon';

// Normalize event data for modal display - matches ScoutIQ dashboard format
function normalizeEventForModal(event) {
  if (!event) return null;
  
  // Derive start from various backend field names
  const start = event.start 
    || event.startDatetime 
    || event.startDate 
    || event.start_date 
    || event.eventDate 
    || event.dueDate;
  
  // Derive end from various backend field names
  const end = event.end 
    || event.endDatetime 
    || event.endDate 
    || event.end_date;
  
  return {
    ...event,
    start,
    end,
    title: event.title || event.name || 'Untitled Event',
    location: event.location || event.eventLocation || '',
    status: event.status || '',
    description: event.description || event.eventDescription || '',
    tags: event.tags || []
  };
}

/**
 * CalendarComponent – Grouped sidebar + assignment completion logic
 *
 * Overview:
 *  - Groups events and assignments for the currently selected date by class (endeavorName).
 *  - Includes BOTH standalone assignments and those linked to events (via eventId) occurring on that date.
 *  - Completed assignments remain visible; there is no filtering that hides them after toggle.
 *
 * Completion Flow:
 *  - Optimistic UI: we flip the local assignment.completed flag immediately.
 *  - Mark complete => PATCH via markAssignmentCompleted(id).
 *  - Mark incomplete => PUT via updateAssignment(id, fullPayload) because backend expects full entity for undo.
 *  - After either path we call refreshAssignments() to force a clean refetch (prevents stale cache issues).
 *  - DataContext handles cache invalidation for user assignments so the refreshed list reflects truth.
 *
 * Celebration Animation:
 *  - recentlyCompletedId stores the assignment id for ~1500ms.
 *  - Assignment row gets class `just-completed` + a transient <span class="completion-confetti"/> element.
 *  - CSS handles pulse & confetti burst; purely decorative (aria-hidden) for accessibility.
 *
 * Accessibility:
 *  - Completion control is a button with role="checkbox" + aria-checked to expose state to AT.
 *  - Row click navigates; internal click on completion control is stopped to avoid navigation.
 *  - Decorative animation elements use aria-hidden to avoid noise.
 *
 * Date Parsing & Robustness:
 *  - parseEventDate cleans malformed ISO strings (duplicate 'T' segments, extra timezone fragments).
 *  - Duplicate assignment display is prevented with a Set tracking seen ids when composing classGroups.
 *
 * Extensibility Notes:
 *  - Add new event types by extending CSS: .event-type-{lowercasedType} for stripe colors.
 *  - Provide a user preference to disable celebrations by gating recentlyCompletedId usage.
 *  - Consider reduced motion: wrap confetti styles in @media (prefers-reduced-motion: no-preference).
 *
 * Edge Cases Covered:
 *  - Malformed datetime strings.
 *  - Assignments linked to events but due same day only included once.
 *  - Undo of completion triggers full refresh ensuring consistent strike-through removal.
 */

function CalendarComponent({
  onDateSelect,
  onCreateEvent,
  courseEvents,          // array of events when in course/schedule preview mode
  courseAssignments,     // NEW: array of assignments (for schedule preview)
  onEditItem,
  onDeleteItem,
  mode = 'global',       // 'global' | 'course'
  headerTitle,
  scopeEndeavorId,
  onEventClick,
  onAssignmentClick,     // optional assignment click override
  enableSplitLayout = false, // NEW: enable ScoutIQ two-column layout
  selectedDate: selectedDateProp
}) {
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState([]); // internal normalized events
  const [assignments, setAssignments] = useState([]); // internal normalized assignments (course + global)
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // month | week | day
  // Track optimistic assignment completion states to prevent needing two clicks when backend/cache refresh is slow.
  // Map<assignmentId, boolean>
  const [optimisticAssignments, setOptimisticAssignments] = useState(() => new Map());
  // Legend filter state for ScoutIQ split layout (which item types to show)
  const [legendFilter, setLegendFilter] = useState({ events: true, assignments: true });
  const effectiveCompleted = useCallback((a) => {
    const aid = a.assignmentId || a.id;
    return optimisticAssignments.has(aid) ? optimisticAssignments.get(aid) : !!a.completed;
  }, [optimisticAssignments]);

  // Helper to get a random Scout relaxation image for empty states
  const getScoutRelaxImage = useCallback(() => {
    const images = [
      { src: '/scout_images/scout_arcade.png', alt: 'Scout playing arcade' },
      { src: '/scout_images/scout_bench.png', alt: 'Scout relaxing on bench' },
      { src: '/scout_images/scout_tv.png', alt: 'Scout watching TV' },
      { src: '/scout_images/scout_vacation.png', alt: 'Scout on vacation' },
      { src: '/scout_images/scout_calendar.png', alt: 'Scout with calendar' }
    ];
    // Use date as seed for consistent image per day
    const dayOfYear = Math.floor((date - new Date(date.getFullYear(), 0, 0)) / 86400000);
    return images[dayOfYear % images.length];
  }, [date]);

  const { user, isInitialized } = useAuth();
  const { getEndeavorsByUser, getFilteredEvents, getFilteredAssignments, isLoading, generateCacheKey, markAssignmentCompleted, updateAssignment, deleteEvent, deleteEventSeries, deleteAssignment } = useData();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const fetchInProgress = useRef(false);
  const lastFetchedUserId = useRef(null);
  const hasMountedOnce = useRef(false); // Track if component has mounted to ensure initial fetch
  const twoColumnContainerRef = useRef(null); // Ref for scrolling to two-column layout

  // Modal state
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const userId = user?.id || user?.userId;
  const externalCourseMode = Array.isArray(courseEvents); // if provided, skip internal event fetch
  const cacheKey = !externalCourseMode && userId
    ? generateCacheKey('user-endeavors', { userId, page: 0, size: 10 })
    : null;
  const loading = cacheKey ? isLoading(cacheKey) : false;

  // Parse utility (more defensive – supports malformed duplicates like
  // "2025-10-13T09:00:00+00:00T00:00:00" by trimming extra segment after the offset)
  const parseEventDate = useCallback((dateString) => {
    if (!dateString) return null;
    let cleaned = String(dateString).trim();

    // If we see more than one 'T', attempt to drop trailing extras.
    // Example malformed: 2025-10-13T09:00:00+00:00T00:00:00
    // Split on 'T' => ['2025-10-13','09:00:00+00:00','00:00:00'] => keep first two parts.
    if ((cleaned.match(/T/g) || []).length > 1) {
      const parts = cleaned.split('T');
      if (parts.length >= 3) {
        // Heuristic: if second part contains a timezone (+/- or Z) and third part looks like a time, drop the third part.
        const second = parts[1];
        const third = parts[2];
        const looksLikeTime = /^\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(third);
        const hasTzInSecond = /[+-]\d{2}:?\d{2}|Z/.test(second);
        if (looksLikeTime && hasTzInSecond) {
          cleaned = parts[0] + 'T' + second; // discard trailing time
        } else {
          // Fallback: just keep first two segments
            cleaned = parts.slice(0,2).join('T');
        }
      }
      // Remove any accidental double 'T' sequences
      cleaned = cleaned.replace(/T{2,}/g, 'T');
    }

    // If still unparseable on first attempt, try removing anything after a second timezone pattern
    let d = new Date(cleaned);
    if (isNaN(d.getTime())) {
      // Remove trailing non-date characters
      cleaned = cleaned.replace(/[^0-9TZ:+-].*$/, '');
      // If has multiple timezone indicators, keep up to first
      const tzMatch = cleaned.match(/([+-]\d{2}:?\d{2}|Z)/);
      if (tzMatch) {
        const idx = cleaned.indexOf(tzMatch[0]);
        cleaned = cleaned.slice(0, idx + tzMatch[0].length);
      }
      d = new Date(cleaned);
    }
    if (isNaN(d.getTime())) {
      // Last resort: if it looks like a bare date, append midnight UTC
      if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
        d = new Date(cleaned + 'T00:00:00');
      }
    }
    return isNaN(d.getTime()) ? null : d;
  }, []);

  // If external (course) events provided, map them once & whenever prop changes
  useEffect(() => {
    if (!externalCourseMode) return;
    // Provide synthesized startDatetime and id for preview events to enable stable assignment grouping
    const mapped = (courseEvents||[]).map(ev => {
      const baseDate = ev.startDatetime || ev.eventDate || (ev.date ? `${ev.date}T${ev.startTime||'00:00:00'}` : null);
      // Build naive HH:mm:ss if only HH:mm provided
      const startDt = baseDate && /T\d{2}:\d{2}$/.test(baseDate) ? baseDate + ':00' : baseDate;
      const dateObj = parseEventDate(startDt);
      // Synthetic id when no id yet (before persistence). Use sequence if available, else fallback to index later.
      const syntheticId = ev.eventId || ev.id || (ev.sequence != null ? `seq-${ev.sequence}` : null);
      return {
        ...ev,
        id: syntheticId || ev.id,
        eventId: ev.eventId || syntheticId,
        startDatetime: startDt,
        date: dateObj,
        endeavorName: ev.endeavorName || ev.courseName || ev.endeavor?.name || ''
      };
    });

    // Map assignments with due date objects. Prefer canonical `dueDate` (YYYY-MM-DD) first.
    const mappedAssignmentsRaw = (courseAssignments||[]).map((a, idx) => {
      // prefer explicit date-only first, then datetime fields
      const dueRaw = a.dueDate || a.dueDatetime || a.date || null;
      let dueDt = null;
      if (dueRaw) {
        // If it's a bare YYYY-MM-DD -> make an explicit midnight local datetime
        if (/^\d{4}-\d{2}-\d{2}$/.test(dueRaw)) {
          dueDt = dueRaw + 'T00:00:00';
        } else if (/T\d{2}:\d{2}/.test(dueRaw)) {
          // If only HH:mm provided (no seconds), append :00
          dueDt = /T\d{2}:\d{2}$/.test(dueRaw) ? dueRaw + ':00' : dueRaw;
        } else {
          dueDt = dueRaw;
        }
      }
      const dueDateObj = parseEventDate(dueDt || dueRaw);
      const aid = a.assignmentId || a.id || `preview-ass-${idx}`;
      return {
        ...a,
        dueDatetime: dueDt || null,
        dueDateObj,
        title: a.title || a.name || 'Assignment',
        id: aid
      };
    });

    // Link assignments to events by sessionSequence first, then by same-day date
    const assignmentsWithLink = mappedAssignmentsRaw.map(a => {
      if (a.eventId || a.eventID || a.event_id) return a; // already linked from backend
      if (a.sessionSequence != null) {
        const bySeq = mapped.find(ev => ev.sequence != null && Number(ev.sequence) === Number(a.sessionSequence));
        if (bySeq) return { ...a, calendarEventMatch: bySeq.eventId || bySeq.id };
      }
      if (a.dueDateObj) {
        const sameDay = mapped.find(ev => ev.date && ev.date.toDateString() === a.dueDateObj.toDateString());
        if (sameDay) return { ...a, calendarEventMatch: sameDay.eventId || sameDay.id };
      }
      return a;
    });

    console.log('[Calendar] external course mode mapping - events:', mapped.length, 'assignments:', mappedAssignmentsRaw.length);
    setEvents(mapped);
    setAssignments(assignmentsWithLink);
  }, [courseEvents, courseAssignments, externalCourseMode, parseEventDate]);

  // Sync parent-controlled selected date into internal calendar state
  useEffect(() => {
    if (!selectedDateProp) return;
    try {
      const d = selectedDateProp instanceof Date ? selectedDateProp : new Date(selectedDateProp);
      if (!isNaN(d.getTime())) setDate(d);
    } catch (e) { /* ignore invalid */ }
  }, [selectedDateProp]);

  // Only fetch when in global mode (no external events)
  // eslint-disable-next-line no-unused-vars
  const loadEndeavors = useCallback(async () => {
    if (externalCourseMode || !userId) return [];
    try {
      const data = await getEndeavorsByUser(userId);
      const userEndeavors = Array.isArray(data) ? data : (data.content || []);
      return userEndeavors;
    } catch (error) {
      console.error('Calendar: Error loading endeavors:', error);
      throw error;
    }
  }, [userId, getEndeavorsByUser, externalCourseMode]);

  // Aggregated user events fetch (replaces per-endeavor loop)
  const dateWindowRef = useRef(null);
  const loadAggregatedEvents = useCallback(async () => {
    if (externalCourseMode) return; // external events passed in
    if (!userId) return;
    
    setEventsLoading(true);
    setEventsError(null);
    try {
      if (!dateWindowRef.current) {
        const now = new Date();
        const anchor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const past = new Date(anchor.getTime() - 365 * 24 * 60 * 60 * 1000); // 1 year back
        const future = new Date(anchor.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year ahead
        dateWindowRef.current = {
          startDate: past.toISOString(),
          endDate: future.toISOString()
        };
      }
      const params = {
        startDate: dateWindowRef.current.startDate,
        endDate: dateWindowRef.current.endDate
      };
      const list = await getFilteredEvents(params);
      
      const nameById = new Map();
      try {
        const endeavorData = await getEndeavorsByUser(userId);
        const endeavorsList = Array.isArray(endeavorData) ? endeavorData : (endeavorData.content || []);
        endeavorsList.forEach(e => nameById.set(Number(e.endeavorId || e.id), e.name));
      } catch { /* non-fatal */ }
      const enriched = list.map(ev => {
        const eid = ev.endeavorId || ev.endeavorID || ev.endeavor_id;
        return {
          ...ev,
          endeavorName: ev.endeavorName || nameById.get(Number(eid)) || '',
          endeavorId: eid,
          eventType: ev.eventTypeName || ev.eventType || 'other',
          date: parseEventDate(ev.startDatetime || ev.eventDate)
        };
      });
      setEvents(enriched);

      // Also fetch assignments in same window for unified calendar
      // Fetch both completed and incomplete assignments so completed ones remain visible & reversible
      const assignmentParams = {
        dueStart: dateWindowRef.current.startDate,
        dueEnd: dateWindowRef.current.endDate
      };
      try {
        const aList = await getFilteredAssignments(assignmentParams);
        const mapped = aList.map(a => {
          const aEndeavorId = a.endeavorId || a.endeavorID || a.endeavor_id;
          return {
            ...a,
            endeavorName: a.endeavorName || nameById.get(Number(aEndeavorId)) || '',
            dueDateObj: parseEventDate(a.dueDatetime || a.dueDate || a.date),
            title: a.title || a.name || 'Assignment'
          };
        });
        setAssignments(mapped);
      } catch (assignErr) {
        console.warn('Calendar: failed to load assignments', assignErr);
      }
    } catch (error) {
      setEventsError(error);
    } finally {
      setEventsLoading(false);
    }
  }, [externalCourseMode, userId, getFilteredEvents, getFilteredAssignments, getEndeavorsByUser, parseEventDate]);

  const fetchEventsAndEndeavors = useCallback(async (forceRefresh = false) => {
    if (externalCourseMode || !userId) return;
    
    // Prevent concurrent fetches
    if (fetchInProgress.current && !forceRefresh) return;
    
    fetchInProgress.current = true;
    try {
      await loadAggregatedEvents();
      lastFetchedUserId.current = userId;
      hasMountedOnce.current = true;
    } catch (error) {
      setEventsError(error);
    } finally {
      fetchInProgress.current = false;
    }
  }, [externalCourseMode, userId, loadAggregatedEvents]);
  // Detect hover capability (disable hover motion on touch/mobile to avoid flicker)
  const canHover = useMemo(() => { // eslint-disable-line no-unused-vars
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }, []);

  useEffect(() => {
    if (!externalCourseMode && isInitialized && userId) {
      // Force fetch on every mount to ensure calendar is never blank
      fetchEventsAndEndeavors(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, userId, externalCourseMode]);

  // Start-of-month for controlling visible month (prevents overflow issues)
  const activeMonthStart = useMemo(
    () => new Date(date.getFullYear(), date.getMonth(), 1),
    [date]
  );

  // Calculate number of week rows for current month (4, 5, or 6)
  const monthWeekCount = useMemo(() => {
    const year = activeMonthStart.getFullYear();
    const month = activeMonthStart.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Get day of week (0=Sunday) for first and last day
    const firstDayOfWeek = firstDay.getDay();
    const lastDayOfMonth = lastDay.getDate();
    
    // Calculate total cells needed (days before month start + days in month)
    const totalCells = firstDayOfWeek + lastDayOfMonth;
    
    // Calculate weeks needed (round up to nearest week)
    return Math.ceil(totalCells / 7);
  }, [activeMonthStart]);

  // Apply dynamic grid-template-rows to calendar grid based on week count
  useEffect(() => {
    if (viewMode !== 'month') return;
    // ScoutIQ split layout uses CSS grid-auto-rows instead — skip inline override
    if (enableSplitLayout) return;
    
    const applyGridRows = () => {
      const grids = document.querySelectorAll('.react-calendar__month-view__days');
      grids.forEach(grid => {
        // Use minmax(100px, auto) for comfortable min-height without bunching
        grid.style.gridTemplateRows = `repeat(${monthWeekCount}, minmax(100px, auto))`;
      });
    };
    
    // Apply immediately and after a brief delay to catch late renders
    applyGridRows();
    const timer = setTimeout(applyGridRows, 50);
    
    return () => clearTimeout(timer);
  }, [monthWeekCount, viewMode, date, enableSplitLayout]);

  const handleActiveStartDateChange = ({ activeStartDate }) => {
    if (!activeStartDate || viewMode !== 'month') return;
    setDate(prev => {
      const desiredDay = prev.getDate();
      const y = activeStartDate.getFullYear();
      const m = activeStartDate.getMonth();
      const lastDay = new Date(y, m + 1, 0).getDate();
      return new Date(y, m, Math.min(desiredDay, lastDay));
    });
  };

  const handleDateClick = (clickedDate) => {
    // If selecting a tile in month view, just set that exact date
    setDate(new Date(clickedDate));
    onDateSelect?.(clickedDate);
    
    // Scroll to two-column container if in ScoutIQ layout — only if not already visible
    if (enableSplitLayout && twoColumnContainerRef.current) {
      setTimeout(() => {
        const el = twoColumnContainerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.top < window.innerHeight * 0.85;
        if (!isVisible) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 200);
    }
  };

  const handleCreateEventClick = () => {
    if (onCreateEvent) {
      onCreateEvent(date);
    } else {
      setShowCreateModal(true);
    }
  };

  // Handle successful creation from AddToCalendarModal
  const handleModalCreated = ({ type, item }) => {
    // Optimistically add to local state
    if (type === 'event') {
      setEvents(prev => [...prev, item]);
      addToast({ 
        message: 'Event created successfully!', 
        intent: 'success', 
        duration: 3000 
      });
    } else if (type === 'assignment') {
      setAssignments(prev => [...prev, item]);
      addToast({ 
        message: 'Assignment created successfully!', 
        intent: 'success', 
        duration: 3000 
      });
    }
    
    // Close modal
    setShowCreateModal(false);
    
    // Refresh events/assignments in background to validate
    if (!externalCourseMode) {
      fetchEventsAndEndeavors(true);
    }
  };

  const handleEventItemClick = (event) => {
    // If parent provided an edit handler, use it (ScheduleStep passes openDraftItemModal)
    const id = event?.id || event?.eventId;
    if (onEditItem) {
      onEditItem('event', id);
      return;
    }
    if (onEventClick) {
      onEventClick(event);
      return;
    }
    // Open modal instead of navigating - normalize data for modal display
    setSelectedEvent(normalizeEventForModal(event));
  };

  const handleAssignmentItemClick = (assignment) => {
    // In course mode, dispatch event for ScoutIQ to handle
    if (mode === 'course' || externalCourseMode) {
      try {
        window.dispatchEvent(new CustomEvent('scoutiq:openAssignmentModal', {
          detail: { assignment }
        }));
      } catch (e) {
        console.warn('Failed to dispatch assignment modal event:', e);
      }
      return;
    }
    const id = assignment?.assignmentId || assignment?.id;
    if (onEditItem) {
      onEditItem('assignment', id);
      return;
    }
    if (onAssignmentClick) {
      onAssignmentClick(assignment);
      return;
    }
    // Open modal instead of navigating
    setSelectedAssignment(assignment);
  };

  // Week/day helpers (unchanged)
  const getWeekDays = useCallback(() => {
    const currentDate = new Date(date);
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      return d;
    });
  }, [date]);

  const navigatePrevious = () => {
    setDate(prev => {
      if (viewMode === 'day') {
        const d = new Date(prev);
        d.setDate(prev.getDate() - 1);
        return d;
      }
      if (viewMode === 'week') {
        const d = new Date(prev);
        d.setDate(prev.getDate() - 7);
        return d;
      }
      // month view: always go to first day of previous month
      return new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
    });
  };

  const navigateNext = () => {
    setDate(prev => {
      if (viewMode === 'day') {
        const d = new Date(prev);
        d.setDate(prev.getDate() + 1);
        return d;
      }
      if (viewMode === 'week') {
        const d = new Date(prev);
        d.setDate(prev.getDate() + 7);
        return d;
      }
      // month view: first day of next month
      return new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
    });
  };
  const goToToday = () => setDate(new Date());

  const formatEventTime = (dateString) => {
    if (!dateString) return '';
    const d = parseEventDate(dateString);
    if (!d) return '';
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const selectedDateEvents = events.filter(ev => ev.date && ev.date.toDateString() === date.toDateString());
  const selectedDateAssignments = assignments.filter(a => a.dueDateObj && a.dueDateObj.toDateString() === date.toDateString());
  if (assignments && assignments.length) {
    try {
      console.log('CALENDAR assignment sample date fields', assignments.slice(0,3).map(assignment => ({ id: assignment.assignmentId || assignment.id, dueDate: assignment.dueDate, dueDatetime: assignment.dueDatetime, date: assignment.date, createdAt: assignment.createdAt })));
    } catch (e) {
      /* ignore logging errors */
    }
  }

  const MAX_EVENTS_PER_DAY = 3; // eslint-disable-line no-unused-vars

  // ScoutIQ Dashboard: Compact tile content (icon + number only)
  const getTileContentScoutIQ = ({ date: day, view }) => {
    if (view !== 'month') return null;
    
    const dayEvents = events.filter(ev => {
      if (!ev.date) return false;
      return ev.date.toDateString() === day.toDateString();
    });
    
    const uniqueEventIds = new Set();
    const uniqueDayEvents = dayEvents.filter(ev => {
      const id = ev.eventId || ev.id;
      if (!id || uniqueEventIds.has(id)) return false;
      uniqueEventIds.add(id);
      return true;
    });
    
    const dayAssignments = assignments.filter(a => {
      if (!a.dueDateObj) return false;
      return a.dueDateObj.toDateString() === day.toDateString();
    });
    
    const uniqueAssignmentIds = new Set();
    const uniqueDayAssignments = dayAssignments.filter(a => {
      const id = a.assignmentId || a.id;
      if (!id || uniqueAssignmentIds.has(id)) return false;
      uniqueAssignmentIds.add(id);
      return true;
    });
    
    const eventCount = uniqueDayEvents.length;
    const assignmentCount = uniqueDayAssignments.length;

    // Apply legend filter to visible counts
    const showEvents = legendFilter.events && eventCount > 0;
    const showAssignments = legendFilter.assignments && assignmentCount > 0;

    if (!showEvents && !showAssignments) return null;

    // Compute heat level for density badge styling
    const visibleTotal = (showEvents ? eventCount : 0) + (showAssignments ? assignmentCount : 0);
    const heat = visibleTotal >= 5 ? 'hot' : visibleTotal >= 3 ? 'warm' : 'cool';

    return (
      <div className="month-tile-counts" data-heat={heat}>
        {showEvents && (
          <div className="tile-count-row event-count-row">
            <span className="tile-count-icon" aria-hidden="true">📅</span>
            <span className="tile-count-number">{eventCount > 9 ? '9+' : eventCount}</span>
          </div>
        )}
        {showAssignments && (
          <div className="tile-count-row assignment-count-row">
            <span className="tile-count-icon" aria-hidden="true">📝</span>
            <span className="tile-count-number">{assignmentCount > 9 ? '9+' : assignmentCount}</span>
          </div>
        )}
      </div>
    );
  };

  const getTileContent = ({ date: day, view }) => {
    if (view !== 'month') return null;
    
    // Calculate event and assignment counts for this day
    // Filter events the same way as in the detail panel to ensure consistency
    const dayEvents = events.filter(ev => {
      if (!ev.date) return false;
      return ev.date.toDateString() === day.toDateString();
    });
    
    // Deduplicate events by ID to avoid counting the same event multiple times
    const uniqueEventIds = new Set();
    const uniqueDayEvents = dayEvents.filter(ev => {
      const id = ev.eventId || ev.id;
      if (!id || uniqueEventIds.has(id)) return false;
      uniqueEventIds.add(id);
      return true;
    });
    
    // Count ALL assignments for this day (both standalone and linked to events)
    // This matches how the detail panel displays assignments
    const dayAssignments = assignments.filter(a => {
      if (!a.dueDateObj) return false;
      return a.dueDateObj.toDateString() === day.toDateString();
    });
    
    // Deduplicate assignments by ID
    const uniqueAssignmentIds = new Set();
    const uniqueDayAssignments = dayAssignments.filter(a => {
      const id = a.assignmentId || a.id;
      if (!id || uniqueAssignmentIds.has(id)) return false;
      uniqueAssignmentIds.add(id);
      return true;
    });
    
    const eventCount = uniqueDayEvents.length;
    const assignmentCount = uniqueDayAssignments.length;

    // If both counts are zero, render nothing (blank tile)
    if (eventCount === 0 && assignmentCount === 0) return null;

    // Render compact icon+count rows
    return (
      <div className="month-tile-counts">
        {eventCount > 0 && (
          <div className="tile-count-row event-count-row">
            <span className="tile-count-icon" aria-hidden="true">📅</span>
            <span className="tile-count-text">Events: {eventCount}</span>
          </div>
        )}
        {assignmentCount > 0 && (
          <div className="tile-count-row assignment-count-row">
            <span className="tile-count-icon" aria-hidden="true">📝</span>
            <span className="tile-count-text">Assignments: {assignmentCount}</span>
          </div>
        )}
      </div>
    );
  };

  const getTileClassName = ({ date: day, view }) => {
    if (view !== 'month') return null;
    const dayEvents = events.filter(ev => ev.date && ev.date.toDateString() === day.toDateString());
    const dayAssignments = assignments.filter(a => a.dueDateObj && a.dueDateObj.toDateString() === day.toDateString() && !(a.eventId || a.eventID || a.event_id || a.calendarEventMatch));
    const count = dayEvents.length + dayAssignments.length;
    return count ? (count > 1 ? 'has-events multiple-events' : 'has-events') : null;
  };

  const formatDateHeader = () => {
    if (viewMode === 'day') {
      return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    }
    if (viewMode === 'week') {
      const week = getWeekDays();
      const start = week[0];
      const end = week[6];
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const isAnyLoading = (loading || eventsLoading) && !externalCourseMode;
  const anyError = eventsError;

  const initializing = (!isInitialized && !externalCourseMode);

  const showViewSelector = !enableSplitLayout; // Hide Month/Week toggle for ScoutIQ course calendar

  // Group assignments by event for nested display (attached assignments). Standalone assignments (no eventId) show in combined list.
  const assignmentsByEventId = useMemo(() => {
    const map = new Map();
    assignments.forEach(a => {
      const evId = a.eventId || a.eventID || a.event_id || a.calendarEventMatch;
      if (!evId) return;
      if (!map.has(evId)) map.set(evId, []);
      map.get(evId).push(a);
    });
    map.forEach(list => list.sort((a,b)=>(a.dueDatetime||'').localeCompare(b.dueDatetime||'')));
    return map;
  }, [assignments]);

  // (Removed expansion panel state and lazy description fetch - assignment rows now navigate directly)

  // Toggle assignment completion (optimistic). Uses PATCH complete endpoint when marking done,
  // falls back to updateAssignment for un-complete.
  // Removed secondary celebration animation (confetti / pulse) to avoid double animations disrupting checkbox state.
  // If reintroduced, restore: recentlyCompletedId logic + CSS classes.
  // const [recentlyCompletedId, setRecentlyCompletedId] = useState(null);

  const refreshAssignments = useCallback(async () => {
    if (!dateWindowRef.current) return;
    try {
      const params = {
        dueStart: dateWindowRef.current.startDate,
        dueEnd: dateWindowRef.current.endDate
      };
      const aList = await getFilteredAssignments(params);
      const mapped = aList.map(a => ({
        ...a,
        dueDateObj: parseEventDate(a.dueDatetime || a.dueDate || a.date),
        title: a.title || a.name || 'Assignment'
      }));
      setAssignments(mapped);
      // Reconcile optimistic map: drop entries that now match server truth to avoid stale overrides.
      setOptimisticAssignments(prev => {
        if (prev.size === 0) return prev;
        const next = new Map(prev);
        mapped.forEach(a => {
          const aid = a.assignmentId || a.id;
          if (next.has(aid) && next.get(aid) === !!a.completed) {
            next.delete(aid);
          }
        });
        return next;
      });
    } catch (e) {
      console.warn('Refresh assignments failed', e);
    }
  }, [getFilteredAssignments, parseEventDate]);

  const handleToggleAssignmentComplete = useCallback(async (assignment) => {
    if (!assignment) return;
    const id = assignment.assignmentId || assignment.id;
    if (!id) return;
    const currentCompleted = effectiveCompleted(assignment); // use effective state not raw prop
    const nextCompleted = !currentCompleted;
    // Optimistic flip using effective state
    setAssignments(prev => prev.map(a => {
      const aid = a.assignmentId || a.id;
      if (aid === id) return { ...a, completed: nextCompleted }; // store next in base model for future renders
      return a;
    }));
    setOptimisticAssignments(prev => {
      const next = new Map(prev);
      next.set(id, nextCompleted);
      return next;
    });
    console.log('[Calendar] toggle start', { id, currentCompleted, nextCompleted });
    try {
      if (!currentCompleted) {
        // Mark complete
        const resp = await markAssignmentCompleted(id);
        if (!resp.ok) throw new Error(`Complete failed (${resp.status})`);
        // Only refresh in global mode; course mode gets updates via useEffect
        if (mode !== 'course' && !externalCourseMode) {
          await refreshAssignments();
        }
        
        // Dispatch event to notify ScoutIQ and other components
        window.dispatchEvent(new CustomEvent('scoutiq:assignmentUpdated', {
          detail: { 
            assignmentId: id, 
            updatedAssignment: { ...assignment, completed: true, status: 'Completed' },
            action: 'completed'
          }
        }));
      } else {
        // Mark incomplete: send full payload to satisfy potential PUT requirements
        const fullPayload = {
          assignmentId: id,
          title: assignment.title || assignment.name,
          description: assignment.description ?? null,
          assignedDate: assignment.assignedDate ?? null,
          dueDatetime: assignment.dueDatetime || assignment.dueDate || assignment.date || null,
          endeavorId: assignment.endeavorId || assignment.endeavorID || assignment.endeavor_id || null,
          eventId: assignment.eventId || assignment.eventID || assignment.event_id || assignment.calendarEventMatch || null,
          priority: assignment.priority || null,
          completed: false,
          completedDatetime: null,
          createdAt: assignment.createdAt ?? null,
          updatedAt: assignment.updatedAt ?? null
        };
        const resp = await updateAssignment(id, fullPayload);
        if (!resp.ok) throw new Error(`Un-complete failed (${resp.status})`);
        // Only refresh in global mode; course mode gets updates via useEffect
        if (mode !== 'course' && !externalCourseMode) {
          await refreshAssignments();
        }
        
        // Dispatch event to notify ScoutIQ and other components
        window.dispatchEvent(new CustomEvent('scoutiq:assignmentUpdated', {
          detail: { 
            assignmentId: id, 
            updatedAssignment: fullPayload,
            action: 'uncompleted'
          }
        }));
      }
      console.log('[Calendar] toggle success', { id, finalCompleted: nextCompleted });
    } catch (err) {
      console.error('Assignment completion toggle failed', err);
      // Rollback
      setAssignments(prev => prev.map(a => {
        const aid = a.assignmentId || a.id;
        if (aid === id) return { ...a, completed: currentCompleted }; // revert to previous effective state
        return a;
      }));
      setOptimisticAssignments(prev => {
        const next = new Map(prev);
        // Restore previous optimistic state (remove override if it mismatched)
        next.set(id, currentCompleted);
        return next;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markAssignmentCompleted, updateAssignment, refreshAssignments, effectiveCompleted]);

  // Build unified selected date list (events + only standalone assignments) used by side panels
  const combinedSelected = [
    ...selectedDateEvents.map(ev => ({ kind:'event', item: ev, sortKey: (ev.startDatetime||ev.eventDate)||'' })),
    ...selectedDateAssignments
      .filter(a => !(a.eventId || a.eventID || a.event_id || a.calendarEventMatch))
      .map(a => ({ kind:'assignment', item: a, sortKey: (a.dueDatetime||'') }))
  ].sort((a,b)=> a.sortKey.localeCompare(b.sortKey));

  // Group by class (endeavor) – include events and ALL assignments (standalone + attached)
  const classGroups = useMemo(() => {
    const evMap = new Map(events.map(ev => [ev.eventId || ev.id, ev]));
    const groupMap = new Map();
    const addToGroup = (className, type, item) => {
      const key = className || 'Uncategorized';
      if (!groupMap.has(key)) {
        groupMap.set(key, { className: key, events: [], assignments: [] });
      }
      groupMap.get(key)[type].push(item);
    };

    // Selected date events first
    selectedDateEvents.forEach(ev => {
      addToGroup(ev.endeavorName || 'Personal', 'events', ev);
    });

    const seenAssignments = new Set();

    // Include assignments attached to those events (due same day)
    selectedDateEvents.forEach(ev => {
      const evId = ev.eventId || ev.id;
      const linked = assignmentsByEventId.get(evId) || [];
      linked.forEach(a => {
        if (!a.dueDateObj || a.dueDateObj.toDateString() !== date.toDateString()) return;
        const aid = a.assignmentId || a.id;
        if (seenAssignments.has(aid)) return;
        seenAssignments.add(aid);
        addToGroup(ev.endeavorName || 'Uncategorized', 'assignments', { ...a, endeavorName: ev.endeavorName });
      });
    });

    // Standalone assignments
    selectedDateAssignments.forEach(a => {
      const evId = a.eventId || a.eventID || a.event_id || a.calendarEventMatch;
      let endeavorName = a.endeavorName;
      if (!endeavorName && evId && evMap.get(evId)) endeavorName = evMap.get(evId).endeavorName;
      const aid = a.assignmentId || a.id;
      if (seenAssignments.has(aid)) return; // avoid duplication if attached already
      seenAssignments.add(aid);
      addToGroup(endeavorName || 'Uncategorized', 'assignments', a);
    });

    // Build sorted groups
    const groups = Array.from(groupMap.values()).sort((g1, g2) => g1.className.localeCompare(g2.className));
    groups.forEach(g => {
      g.events.sort((a,b) => (a.startDatetime || a.eventDate || '').localeCompare(b.startDatetime || b.eventDate || ''));
      g.assignments.sort((a,b) => (a.dueDatetime || '').localeCompare(b.dueDatetime || ''));
    });
    return groups;
  }, [selectedDateEvents, selectedDateAssignments, assignmentsByEventId, date, events]);

  // NOTE: UI visual hierarchy enhancements:
  // - We render grouped class panels with distinct sections for Events (📅) and Assignments (📝)
  // - Each event card includes a colored side stripe whose hue can be themed via CSS variables.
  //   Stripe class mapping uses eventType (lowercased). Extend Calendar.css to adjust colors.

  // Determine view-specific heading
  const getViewHeading = () => { // eslint-disable-line no-unused-vars
    if (headerTitle) return headerTitle;
    if (mode === 'course') return 'Course Events Calendar';
    
    // For global calendar, make heading view-specific
    switch (viewMode) {
      case 'month':
        return 'Monthly View';
      case 'week':
        return 'Weekly View';
      case 'day':
        return 'Daily View';
      default:
        return 'Event Calendar';
    }
  };

  return (
    <div className={`calendar-container ${externalCourseMode ? 'calendar-external-mode' : ''}`}>
      <div className="calendar-header" style={{ position: 'relative', width: '100%' }}>
        <div className="calendar-header-row-2">
          <div className={`calendar-toolbar ${enableSplitLayout ? 'course-calendar-nav' : ''}`}>
            {/* Left cluster: navigation arrows and Today button */}
            <div className={`calendar-toolbar-left ${enableSplitLayout ? 'cc-nav-left' : ''}`}>
              <button
                className={`ui-btn ui-btn--ghost ui-btn--sm cal-nav-btn prev ${enableSplitLayout ? 'cc-nav-btn prev-btn' : ''}`}
                onClick={navigatePrevious}
                title="Previous month/week/day"
                aria-label="Previous"
              >
                ←
              </button>
              {enableSplitLayout && (
                <span className="calendar-period-label cc-month-label">
                  {formatDateHeader()}
                </span>
              )}
              <button
                className={`ui-btn ui-btn--ghost ui-btn--sm cal-nav-btn next ${enableSplitLayout ? 'cc-nav-btn next-btn' : ''}`}
                onClick={navigateNext}
                title="Next month/week/day"
                aria-label="Next"
              >
                →
              </button>
              <button
                className={`ui-btn ui-btn--neutral ui-btn--sm cal-nav-btn today ${enableSplitLayout ? 'cc-today-btn' : ''}`}
                onClick={goToToday}
                aria-label="Go to today"
              >
                Today
              </button>
            </div>
            
            {/* Center: current month/period label (only for non-ScoutIQ) */}
            <div className="calendar-toolbar-center">
              {!enableSplitLayout && (
                <div className="calendar-period-label">
                  {formatDateHeader()}
                </div>
              )}
            </div>
            
            {/* Right cluster: Month/Week view toggle */}
            {showViewSelector && (
              <div className="calendar-toolbar-right">
                <div className="view-mode-selector" role="tablist" aria-label="Calendar view modes">
                  <button
                    className={`cal-view-btn ${viewMode === 'month' ? 'active' : ''}`}
                    onClick={() => setViewMode('month')}
                    role="tab"
                    aria-selected={viewMode === 'month'}
                  >Month</button>
                  <button
                    className={`cal-view-btn ${viewMode === 'week' ? 'active' : ''}`}
                    onClick={() => setViewMode('week')}
                    role="tab"
                    aria-selected={viewMode === 'week'}
                  >Week</button>
                </div>
              </div>
            )}

            {/* Inline Add button for ScoutIQ course calendar */}
            {enableSplitLayout && onCreateEvent && (
              <button
                className="cc-add-btn-inline"
                onClick={() => setShowCreateModal(true)}
                title="Add new event or assignment"
                aria-label="Add new"
              >
                + Add
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="calendar-content">
        {initializing ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Initializing calendar...</p>
          </div>
        ) : isAnyLoading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading events...</p>
          </div>
        ) : anyError ? (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <p>{anyError.message || anyError}</p>
            {!externalCourseMode && (
              <button
                className="ui-btn ui-btn--neutral cal-retry-btn"
                onClick={() => {
                  lastFetchedUserId.current = null;
                  hasMountedOnce.current = false;
                  fetchEventsAndEndeavors(true);
                }}
              >
                🔄 Try Again
              </button>
            )}
          </div>
        ) : (
          <div className="calendar-view-container">
            {viewMode === 'month' && (
              enableSplitLayout ? (
                // ScoutIQ Dashboard: calendar on top, two-column layout below (events | assignments)
                <div className="scoutiq-calendar-layout">
                  <div className="calendar-wrapper">
                    <Calendar
                      onChange={handleDateClick}
                      value={date}
                      activeStartDate={activeMonthStart}
                      onActiveStartDateChange={handleActiveStartDateChange}
                      tileContent={getTileContentScoutIQ}
                      tileClassName={getTileClassName}
                      className="react-calendar"
                      locale="en-US"
                      showNeighboringMonth={true}
                      showNavigation={false}
                      formatShortWeekday={(locale, d) => ['S','M','T','W','T','F','S'][d.getDay()]}
                    />
                    <div className="calendar-legend">
                      <div
                        className={`legend-item ${legendFilter.events ? 'legend-active' : 'legend-inactive'}`}
                        onClick={() => setLegendFilter(prev => ({ ...prev, events: !prev.events }))}
                        role="button"
                        tabIndex={0}
                        aria-pressed={legendFilter.events}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLegendFilter(prev => ({ ...prev, events: !prev.events })); }}}
                      >
                        <span className="legend-icon" aria-hidden="true">📅</span>
                        <span className="legend-label">Events</span>
                      </div>
                      <div
                        className={`legend-item ${legendFilter.assignments ? 'legend-active' : 'legend-inactive'}`}
                        onClick={() => setLegendFilter(prev => ({ ...prev, assignments: !prev.assignments }))}
                        role="button"
                        tabIndex={0}
                        aria-pressed={legendFilter.assignments}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLegendFilter(prev => ({ ...prev, assignments: !prev.assignments })); }}}
                      >
                        <span className="legend-icon" aria-hidden="true">📝</span>
                        <span className="legend-label">Assignments</span>
                      </div>
                    </div>
                  </div>
                  {/* Two-column layout below calendar: Events on left, Assignments on right */}
                  <div ref={twoColumnContainerRef} className="scoutiq-two-column-container">
                    <div className="scoutiq-date-header">
                      <h3>{date.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}</h3>
                    </div>
                    {classGroups.length === 0 ? (
                      <div className="no-events scout-vacation-empty">
                        <img
                          src={getScoutRelaxImage().src}
                          alt={getScoutRelaxImage().alt}
                          className="scout-vacation-img"
                        />
                        <h2 className="scout-vacation-title">Scout's taking the day off</h2>
                        <p className="scout-vacation-subtitle">Nothing scheduled for today. Add something to help Scout keep you on track!</p>
                      </div>
                    ) : (
                      <div className="scoutiq-columns-wrapper">
                        {/* LEFT COLUMN: Events (hidden when legend filter is off) */}
                        {legendFilter.events && (
                        <div className="scoutiq-column scoutiq-events-column">
                          <div className="scoutiq-column-header">
                            <span className="scoutiq-column-icon" aria-hidden="true">📅</span>
                            <span className="scoutiq-column-title">Events</span>
                          </div>
                          <div className="scoutiq-column-content">
                            {classGroups.filter(group => group.events.length > 0).map(group => (
                              <div className="class-group" key={`events-${group.className}`}>
                                {group.className !== 'Uncategorized' && (
                                  <div className="class-group-header" title={group.className}>{group.className}</div>
                                )}
                                <div className="cgs-items">
                                    {group.events.map(ev => {
                                      const etype = (ev.eventType || 'other').toLowerCase();
                                      return (
                                        <motion.div
                                          key={ev.eventId || ev.id}
                                          className={`event-item grouped event-type-${etype}`}
                                          onClick={() => handleEventItemClick(ev)}
                                        >
                                          <div className="event-stripe" aria-hidden="true" />
                                          <div className="event-main">
                                            <div className="event-title-row">
                                              <div className="event-title" title={ev.name || ev.title}>{ev.name || ev.title}</div>
                                              <div className="event-time">{formatEventTime(ev.startDatetime || ev.eventDate)}</div>
                                            </div>
                                            {ev.description && (
                                              <div className="event-description clamp">
                                                {(ev.description.length > 160 ? `${ev.description.slice(0,160)}…` : ev.description)}
                                              </div>
                                            )}
                                          </div>
                                        </motion.div>
                                      );
                                    })}
                                  </div>
                                </div>
                            ))}
                            {classGroups.every(g => g.events.length === 0) && (
                              <div className="scoutiq-empty-column">
                                <p>No events for this day</p>
                              </div>
                            )}
                          </div>
                        </div>
                        )}

                        {/* RIGHT COLUMN: Assignments (hidden when legend filter is off) */}
                        {legendFilter.assignments && (
                        <div className="scoutiq-column scoutiq-assignments-column">
                          <div className="scoutiq-column-header">
                            <span className="scoutiq-column-icon" aria-hidden="true">📝</span>
                            <span className="scoutiq-column-title">Assignments</span>
                          </div>
                          <div className="scoutiq-column-content">
                            {classGroups.filter(group => group.assignments.length > 0).map(group => (
                              <div className="class-group" key={`assignments-${group.className}`}>
                                {group.className !== 'Uncategorized' && (
                                  <div className="class-group-header" title={group.className}>{group.className}</div>
                                )}
                                {(() => {
                                    const total = group.assignments.length;
                                    const done = group.assignments.filter(a => effectiveCompleted(a)).length;
                                    const pct = total > 0 ? (done / total) * 100 : 0;
                                    return (
                                      <div
                                        className="assignment-progress-wrapper"
                                        role="progressbar"
                                        aria-valuenow={done}
                                        aria-valuemin={0}
                                        aria-valuemax={total}
                                        aria-valuetext={`${done} of ${total} assignments complete`}
                                      >
                                        <div className="assignment-progress-bar" title={`${done}/${total} completed`}>
                                          <motion.div
                                            className="assignment-progress-fill"
                                            initial={false}
                                            animate={{ width: `${pct}%` }}
                                            transition={{ duration: 0.5, ease: 'easeOut' }}
                                          />
                                          {done === total && total > 0 && (
                                            <span className="progress-confetti" aria-hidden="true" />
                                          )}
                                        </div>
                                        <div className="assignment-progress-text">{done}/{total} assignments completed</div>
                                      </div>
                                    );
                                  })()}
                                  <ul className="cgs-items assignment-condensed">
                                    {group.assignments.map(a => {
                                      const aid = a.assignmentId || a.id;
                                      const completed = effectiveCompleted(a);
                                      return (
                                        <li
                                          key={aid}
                                          className={`assignment-row condensed ${completed ? 'is-completed' : ''}`}
                                          onClick={(e) => {
                                            if (e.target.closest('.assignment-complete-btn')) return;
                                            handleAssignmentItemClick(a);
                                          }}
                                        >
                                          <span className="assignment-dot" aria-hidden="true" />
                                          <span className="assignment-row-title" title={a.title}>{a.title}</span>
                                          <div className="assignment-actions">
                                            <motion.button
                                              type="button"
                                              className={`assignment-complete-btn motion-btn ${completed ? 'completed' : ''}`}
                                              aria-label={completed ? `Mark ${a.title} incomplete` : `Mark ${a.title} complete`}
                                              aria-checked={completed}
                                              role="checkbox"
                                              title={completed ? `Mark ${a.title} incomplete` : `Mark ${a.title} complete`}
                                              whileTap={{ scale: 1.2 }}
                                              onClick={(e) => { e.stopPropagation(); handleToggleAssignmentComplete(a); }}
                                              style={completed ? {
                                                background: 'linear-gradient(135deg,#3b82f6 0%, #60a5fa 55%, #93c5fd 100%)',
                                                borderColor: '#3b82f6',
                                                boxShadow: '0 0 0 2px rgba(59,130,246,.35), 0 2px 4px rgba(0,0,0,.12)'
                                              } : undefined}
                                            >
                                              <motion.span
                                                className="acb-icon"
                                                initial={false}
                                                animate={completed ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }}
                                                transition={{ duration: 0.3, ease: 'easeOut' }}
                                                aria-hidden="true"
                                              >✓</motion.span>
                                            </motion.button>
                                            <AnimatePresence>
                                              {completed && (
                                                <motion.span
                                                  key="completed-pill"
                                                  className="assignment-completed-pill linked"
                                                  title="Completed"
                                                  initial={{ opacity: 0, x: 8 }}
                                                  animate={{ opacity: 1, x: 0 }}
                                                  exit={{ opacity: 0, x: 8 }}
                                                  transition={{ duration: 0.25, ease: 'easeOut' }}
                                                >Completed</motion.span>
                                              )}
                                            </AnimatePresence>
                                          </div>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                            ))}
                            {classGroups.every(g => g.assignments.length === 0) && (
                              <div className="scoutiq-empty-column">
                                <p>No assignments for this day</p>
                              </div>
                            )}
                          </div>
                        </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Standard month view (global calendar page)
                <div className="month-view-with-events">
                  <div className="calendar-wrapper">
                    <Calendar
                      onChange={handleDateClick}
                      value={date}
                      activeStartDate={activeMonthStart}
                      onActiveStartDateChange={handleActiveStartDateChange}
                      tileContent={getTileContent}
                      tileClassName={getTileClassName}
                      className="react-calendar"
                      locale="en-US"
                      showNeighboringMonth={true}
                      showNavigation={false}
                      formatShortWeekday={(locale, d) => ['S','M','T','W','T','F','S'][d.getDay()]}
                    />
                  </div>
                  <div className="selected-date-events">
                    <div className="selected-date-header">
                      <h3>{date.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}</h3>
                      {!externalCourseMode && (
                        <button
                          className="ui-btn--icon-circular create-event-circle"
                          onClick={handleCreateEventClick}
                          title="Create new event for this day"
                          aria-label="Create new event"
                        >
                          +
                        </button>
                      )}
                    </div>
                    {classGroups.length === 0 ? (
                      <div className="no-events scout-vacation-empty">
                        <img
                          src={getScoutRelaxImage().src}
                          alt={getScoutRelaxImage().alt}
                          className="scout-vacation-img"
                        />
                        <h2 className="scout-vacation-title">Scout's taking the day off</h2>
                        <p className="scout-vacation-subtitle">Nothing scheduled for today. Add something to help Scout keep you on track!</p>
                      </div>
                    ) : (
                      <div className="events-list grouped-by-class">{classGroups.map(group => (
                        <div className="class-group" key={group.className}>
                          <div className="class-group-header" title={group.className}>{group.className}</div>
                          {group.events.length > 0 && (
                            <div className="class-group-section events">
                              <div className="cgs-title"><span className="cgs-icon" aria-hidden="true">📅</span> Events</div>
                              <div className="cgs-items">
                                {group.events.map(ev => {
                                  const etype = (ev.eventType || 'other').toLowerCase();
                                  return (
                                    <motion.div
                                      key={ev.eventId || ev.id}
                                      className={`event-item grouped event-type-${etype}`}
                                      onClick={() => handleEventItemClick(ev)}
                                    >
                                      <div className="event-stripe" aria-hidden="true" />
                                      <div className="event-main">
                                        <div className="event-title-row">
                                          <div className="event-title" title={ev.name || ev.title}>{ev.name || ev.title}</div>
                                          <div className="event-time">{formatEventTime(ev.startDatetime || ev.eventDate)}</div>
                                        </div>
                                        {ev.description && (
                                          <div className="event-description clamp">
                                            {(ev.description.length > 160 ? `${ev.description.slice(0,160)}…` : ev.description)}
                                          </div>
                                        )}
                                      </div>
                                    </motion.div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {group.assignments.length > 0 && (
                            <div className="class-group-section assignments">
                              {(() => {
                                const total = group.assignments.length;
                                const done = group.assignments.filter(a => effectiveCompleted(a)).length;
                                const pct = total > 0 ? (done / total) * 100 : 0;
                                return (
                                  <div
                                    className="assignment-progress-wrapper"
                                    role="progressbar"
                                    aria-valuenow={done}
                                    aria-valuemin={0}
                                    aria-valuemax={total}
                                    aria-valuetext={`${done} of ${total} assignments complete`}
                                  >
                                    <div className="assignment-progress-bar" title={`${done}/${total} completed`}>
                                      <motion.div
                                        className="assignment-progress-fill"
                                        initial={false}
                                        animate={{ width: `${pct}%` }}
                                        transition={{ duration: 0.5, ease: 'easeOut' }}
                                      />
                                      {done === total && total > 0 && (
                                        <span className="progress-confetti" aria-hidden="true" />
                                      )}
                                    </div>
                                    <div className="assignment-progress-text">{done}/{total} assignments completed</div>
                                  </div>
                                );
                              })()}
                              <ul className="cgs-items assignment-condensed">
                                {group.assignments.map(a => {
                                  const aid = a.assignmentId || a.id;
                                  const completed = effectiveCompleted(a);
                                  return (
                                    <li
                                      key={aid}
                                      className={`assignment-row condensed ${completed ? 'is-completed' : ''}`}
                                      onClick={(e) => {
                                        if (e.target.closest('.assignment-complete-btn')) return;
                                        handleAssignmentItemClick(a);
                                      }}
                                    >
                                      <span className="assignment-dot" aria-hidden="true" />
                                      <span className="assignment-row-title" title={a.title}>{a.title}</span>
                                      <div className="assignment-actions">
                                        <motion.button
                                          type="button"
                                          className={`assignment-complete-btn motion-btn ${completed ? 'completed' : ''}`}
                                          aria-label={completed ? `Mark ${a.title} incomplete` : `Mark ${a.title} complete`}
                                          aria-checked={completed}
                                          role="checkbox"
                                          title={completed ? `Mark ${a.title} incomplete` : `Mark ${a.title} complete`}
                                          whileTap={{ scale: 1.2 }}
                                          onClick={(e) => { e.stopPropagation(); handleToggleAssignmentComplete(a); }}
                                          style={completed ? {
                                            background: 'linear-gradient(135deg,#3b82f6 0%, #60a5fa 55%, #93c5fd 100%)',
                                            borderColor: '#3b82f6',
                                            boxShadow: '0 0 0 2px rgba(59,130,246,.35), 0 2px 4px rgba(0,0,0,.12)'
                                          } : undefined}
                                        >
                                          <motion.span
                                            className="acb-icon"
                                            initial={false}
                                            animate={completed ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }}
                                            transition={{ duration: 0.3, ease: 'easeOut' }}
                                            aria-hidden="true"
                                          >✓</motion.span>
                                        </motion.button>
                                        <AnimatePresence>
                                          {completed && (
                                            <motion.span
                                              key="completed-pill"
                                              className="assignment-completed-pill linked"
                                              title="Completed"
                                              initial={{ opacity: 0, x: 8 }}
                                              animate={{ opacity: 1, x: 0 }}
                                              exit={{ opacity: 0, x: 8 }}
                                              transition={{ duration: 0.25, ease: 'easeOut' }}
                                            >Completed</motion.span>
                                          )}
                                        </AnimatePresence>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}</div>
                    )}
                  </div>
                </div>
              )
            )}
            {viewMode === 'week' && (
              <div className="week-view week-with-side">
                <div className="week-grid-header">
                  <div className="week-time-header-spacer" />
                  {getWeekDays().map(d => (
                    <div
                      key={d.toISOString()}
                      className={`week-day-head ${d.toDateString() === date.toDateString() ? 'is-selected' : ''}`}
                      onClick={() => handleDateClick(d)}
                    >
                      <div className="wdh-label">
                        {d.toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      <div className="wdh-date">
                        {d.getDate()}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="week-body">
                  {/* Time axis column */}
                  <div className="week-time-axis">
                    {Array.from({ length: 17 }, (_, i) => {
                      const hour = 8 + i; // 8 AM to 12 AM (midnight = 24, but we show as 12 AM)
                      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                      const period = hour < 12 ? 'AM' : 'PM';
                      const displayHour24 = hour === 24 ? 12 : displayHour;
                      
                      return (
                        <div key={hour} className="week-time-slot">
                          <span className="time-label">{displayHour24} {period}</span>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Day columns */}
                  {getWeekDays().map(d => {
                    const dayEvents = events
                      .filter(ev => ev.date && ev.date.toDateString() === d.toDateString())
                      .sort((a,b) => (a.startDatetime || a.eventDate || '').localeCompare(b.startDatetime || b.eventDate || ''));
                    
                    // Time-based positioning constants
                    const START_HOUR = 8; // 8 AM
                    // eslint-disable-next-line no-unused-vars
                    const END_HOUR = 24; // Midnight (12 AM)
                    const PIXELS_PER_MINUTE = 1; // 1px per minute
                    const MIN_EVENT_HEIGHT = 40; // Minimum height in pixels
                    
                    // Helper to calculate position for an event
                    const getEventPosition = (ev) => {
                      const eventDate = parseEventDate(ev.startDatetime || ev.eventDate);
                      if (!eventDate) return null;
                      
                      const eventHour = eventDate.getHours();
                      const eventMinute = eventDate.getMinutes();
                      const minutesFromStart = (eventHour - START_HOUR) * 60 + eventMinute;
                      
                      // If event is before start hour, position at top
                      const top = Math.max(0, minutesFromStart * PIXELS_PER_MINUTE);
                      
                      // Calculate duration (default to 1 hour if no end time)
                      let durationMinutes = 60;
                      const height = Math.max(MIN_EVENT_HEIGHT, durationMinutes * PIXELS_PER_MINUTE);
                      
                      return { top, height };
                    };
                    
                    return (
                      <div
                        key={`col-${d.toISOString()}`}
                        className={`week-day-column ${d.toDateString() === date.toDateString() ? 'is-selected' : ''}`}
                        onClick={() => handleDateClick(d)}
                      >
                        {dayEvents.map((ev, idx) => {
                          const position = getEventPosition(ev);
                          if (!position) return null;
                          
                          return (
                            <motion.div
                              key={ev.eventId || ev.id || idx}
                              className="week-event-chip"
                              style={{
                                top: `${position.top}px`,
                                height: `${position.height}px`,
                                minHeight: `${MIN_EVENT_HEIGHT}px`
                              }}
                              onClick={(e) => { e.stopPropagation(); handleEventItemClick(ev); }}
                            >
                              <span className="chip-title">{ev.name || ev.title}</span>
                              <span className="chip-time">{formatEventTime(ev.startDatetime || ev.eventDate)}</span>
                            </motion.div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
                {/* Updated week view side panel to use grouped class layout like month/day */}
                <div className="selected-date-events side-panel">
                  <div className="selected-date-header">
                    <h3>{date.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}</h3>
                    {!externalCourseMode && (
                      <button
                        className="ui-btn--icon-circular create-event-circle"
                        onClick={handleCreateEventClick}
                        title="Create new event for this day"
                        aria-label="Create new event"
                      >
                        +
                      </button>
                    )}
                  </div>
                  {classGroups.length === 0 ? (
                    <div className="no-events scout-vacation-empty">
                      <img
                        src={getScoutRelaxImage().src}
                        alt={getScoutRelaxImage().alt}
                        className="scout-vacation-img"
                      />
                      <h2 className="scout-vacation-title">Scout's taking the day off</h2>
                      <p className="scout-vacation-subtitle">Nothing scheduled for today. Add something to help Scout keep you on track!</p>
                    </div>
                  ) : (
                    <div className="events-list grouped-by-class">
                      {classGroups.map(group => (
                        <div className="class-group" key={group.className}>
                          <div className="class-group-header" title={group.className}>{group.className}</div>
                          {group.events.length > 0 && (
                            <div className="class-group-section events">
                              <div className="cgs-title"><span className="cgs-icon" aria-hidden="true">📅</span> Events</div>
                              <div className="cgs-items">
                                {group.events.map(ev => {
                                  const etype = (ev.eventType || 'other').toLowerCase();
                                  return (
                                    <motion.div
                                      key={ev.eventId || ev.id}
                                      className={`event-item grouped event-type-${etype}`}
                                      onClick={() => handleEventItemClick(ev)}
                                    >
                                      <div className="event-stripe" aria-hidden="true" />
                                      <div className="event-main">
                                        <div className="event-title-row">
                                          <div className="event-title" title={ev.name || ev.title}>{ev.name || ev.title}</div>
                                          <div className="event-time">{formatEventTime(ev.startDatetime || ev.eventDate)}</div>
                                        </div>
                                        {ev.description && (
                                          <div className="event-description clamp">
                                            {(ev.description.length > 160 ? `${ev.description.slice(0,160)}…` : ev.description)}
                                          </div>
                                        )}
                                      </div>
                                    </motion.div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {group.assignments.length > 0 && (
                            <div className="class-group-section assignments">
                              {(() => {
                                const total = group.assignments.length;
                                const done = group.assignments.filter(a => effectiveCompleted(a)).length;
                                const pct = total > 0 ? (done / total) * 100 : 0;
                                return (
                                  <div
                                    className="assignment-progress-wrapper"
                                    role="progressbar"
                                    aria-valuenow={done}
                                    aria-valuemin={0}
                                    aria-valuemax={total}
                                    aria-valuetext={`${done} of ${total} assignments complete`}
                                  >
                                    <div className="assignment-progress-bar" title={`${done}/${total} completed`}>
                                      <motion.div
                                        className="assignment-progress-fill"
                                        initial={false}
                                        animate={{ width: `${pct}%` }}
                                        transition={{ duration: 0.5, ease: 'easeOut' }}
                                      />
                                      {done === total && total > 0 && (
                                        <span className="progress-confetti" aria-hidden="true" />
                                      )}
                                    </div>
                                    <div className="assignment-progress-stats">{done}/{total} done</div>
                                  </div>
                                );
                              })()}
                              <div className="cgs-title"><span className="cgs-icon" aria-hidden="true">📝</span> Assignments</div>
                              <div className="due-group-label">
                                {(() => {
                                  const today = new Date();
                                  const sameDay = today.toDateString() === date.toDateString();
                                  return sameDay ? 'Due Today' : `Due ${date.toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' })}`;
                                })()}
                              </div>
                              <ul className="cgs-items assignment-condensed">
                                {group.assignments.map(a => {
                                  const aid = a.assignmentId || a.id;
                                  const completed = effectiveCompleted(a);
                                  return (
                                    <li
                                      key={aid}
                                      className={`assignment-row condensed ${completed ? 'is-completed' : ''}`}
                                      onClick={(e) => {
                                        if (e.target.closest('.assignment-complete-btn')) return;
                                        handleAssignmentItemClick(a);
                                      }}
                                    >
                                      <span className="assignment-dot" aria-hidden="true" />
                                      <span className="assignment-row-title" title={a.title}>{a.title}</span>
                                      <div className="assignment-actions">
                                        <motion.button
                                          type="button"
                                          className={`assignment-complete-btn motion-btn ${completed ? 'completed' : ''}`}
                                          aria-label={completed ? `Mark ${a.title} incomplete` : `Mark ${a.title} complete`}
                                          aria-checked={completed}
                                          role="checkbox"
                                          title={completed ? `Mark ${a.title} incomplete` : `Mark ${a.title} complete`}
                                          whileTap={{ scale: 1.2 }}
                                          onClick={(e) => { e.stopPropagation(); handleToggleAssignmentComplete(a); }}
                                          style={completed ? {
                                            background: 'linear-gradient(135deg,#3b82f6 0%, #60a5fa 55%, #93c5fd 100%)',
                                            borderColor: '#3b82f6',
                                            boxShadow: '0 0 0 2px rgba(59,130,246,.35), 0 2px 4px rgba(0,0,0,.12)'
                                          } : undefined}
                                        >
                                          <motion.span
                                            className="acb-icon"
                                            initial={false}
                                            animate={completed ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }}
                                            transition={{ duration: 0.3, ease: 'easeOut' }}
                                            aria-hidden="true"
                                          >✓</motion.span>
                                        </motion.button>
                                        <AnimatePresence>
                                          {completed && (
                                            <motion.span
                                              key="completed-pill-week"
                                              className="assignment-completed-pill linked"
                                              title="Completed"
                                              initial={{ opacity: 0, x: 8 }}
                                              animate={{ opacity: 1, x: 0 }}
                                              exit={{ opacity: 0, x: 8 }}
                                              transition={{ duration: 0.25, ease: 'easeOut' }}
                                            >Completed</motion.span>
                                          )}
                                        </AnimatePresence>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {viewMode === 'day' && (
              <div className="day-view day-with-side">
                {/* Primary scrollable list of events/assignments for the day (left column) */}
                <div className="day-main-pane">
                  <div className="day-header">
                    <h3>
                      {date.toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </h3>
                    <div className="day-sub">
                      {combinedSelected.length} {combinedSelected.length === 1 ? 'Item' : 'Items'}
                    </div>
                  </div>
                  <div className="day-events-scroll">
                    {combinedSelected.length === 0 ? (
                      <div className="no-events large">
                        <div className="no-events-icon">
                          <CalendarDayIcon date={date} size={36} />
                        </div>
                        <p>No events on this day</p>
                      </div>
                    ) : (
                      combinedSelected.map(({kind,item}) => (
                        <div
                          key={(kind==='event' ? (item.eventId||item.id) : (item.assignmentId||item.id))}
                          className={`day-event-item ${kind==='assignment' ? 'assignment-item' : ''}`}
                          onClick={() => { if (kind==='event') handleEventItemClick(item); else handleAssignmentItemClick(item); }}
                        >
                          <div className="day-event-time">
                            {kind==='event' ? formatEventTime(item.startDatetime || item.eventDate) : (effectiveCompleted(item) ? 'Done' : '')}
                          </div>
                          <div className="day-event-main">
                            <div className="day-event-icon">
                              <span className="dve-icon" aria-hidden="true">
                                <CalendarDayIcon date={kind==='event' ? (item.startDatetime || item.eventDate || date) : (item.dueDatetime || date)} size={24} className="calendar-inline-icon" />
                              </span>
                            </div>
                            <div className="day-event-details">
                              <div className="day-event-title">{item.name || item.title}</div>
                              {kind==='event' && <div className="day-event-endeavor">{item.endeavorName}</div>}
                              {item.description && (
                                <div className="day-event-desc">
                                  {item.description.length > 140 ? `${item.description.slice(0,140)}…` : item.description}
                                </div>
                              )}
                            </div>
                            {kind==='assignment' && <div className="assignment-badge" title="Assignment">A</div>}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                {/* Side panel added for day view mirroring week/month behavior */}
                <div className="selected-date-events side-panel">
                  <div className="selected-date-header">
                    <h3>{date.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}</h3>
                    {!externalCourseMode && (
                      <button
                        className="ui-btn--icon-circular create-event-circle"
                        onClick={handleCreateEventClick}
                        title="Create new event for this day"
                        aria-label="Create new event"
                      >
                        +
                      </button>
                    )}
                  </div>
                  {classGroups.length === 0 ? (
                    <div className="no-events scout-vacation-empty">
                      <img
                        src={getScoutRelaxImage().src}
                        alt={getScoutRelaxImage().alt}
                        className="scout-vacation-img"
                      />
                      <h2 className="scout-vacation-title">Scout's taking the day off</h2>
                      <p className="scout-vacation-subtitle">Nothing scheduled for today. Add something to help Scout keep you on track!</p>
                    </div>
                  ) : (
                    <div className="events-list grouped-by-class">
                      {classGroups.map(group => (
                        <div className="class-group" key={group.className}>
                          <div className="class-group-header" title={group.className}>{group.className}</div>
                          {group.events.length > 0 && (
                            <div className="class-group-section events">
                              <div className="cgs-title"><span className="cgs-icon" aria-hidden="true">📅</span> Events</div>
                              <div className="cgs-items">
                                {group.events.map(ev => {
                                  const etype = (ev.eventType || 'other').toLowerCase();
                                  return (
                                    <motion.div
                                      key={ev.eventId || ev.id}
                                      className={`event-item grouped event-type-${etype}`}
                                      onClick={() => handleEventItemClick(ev)}
                                    >
                                      <div className="event-stripe" aria-hidden="true" />
                                      <div className="event-main">
                                        <div className="event-title-row">
                                          <div className="event-title" title={ev.name || ev.title}>{ev.name || ev.title}</div>
                                          <div className="event-time">{formatEventTime(ev.startDatetime || ev.eventDate)}</div>
                                        </div>
                                        {ev.description && (
                                          <div className="event-description clamp">
                                            {(ev.description.length > 160 ? `${ev.description.slice(0,160)}…` : ev.description)}
                                          </div>
                                        )}
                                      </div>
                                    </motion.div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {group.assignments.length > 0 && (
                            <div className="class-group-section assignments">
                              {(() => {
                                const total = group.assignments.length;
                                const done = group.assignments.filter(a => effectiveCompleted(a)).length;
                                const pct = total > 0 ? (done / total) * 100 : 0;
                                return (
                                  <div
                                    className="assignment-progress-wrapper"
                                    role="progressbar"
                                    aria-valuenow={done}
                                    aria-valuemin={0}
                                    aria-valuemax={total}
                                    aria-valuetext={`${done} of ${total} assignments complete`}
                                  >
                                    <div className="assignment-progress-bar" title={`${done}/${total} completed`}>
                                      <motion.div
                                        className="assignment-progress-fill"
                                        initial={false}
                                        animate={{ width: `${pct}%` }}
                                        transition={{ duration: 0.5, ease: 'easeOut' }}
                                      />
                                      {done === total && total > 0 && (
                                        <span className="progress-confetti" aria-hidden="true" />
                                      )}
                                    </div>
                                    <div className="assignment-progress-stats">{done}/{total} done</div>
                                  </div>
                                );
                              })()}
                              <div className="cgs-title"><span className="cgs-icon" aria-hidden="true">📝</span> Assignments</div>
                              <div className="due-group-label">
                                {(() => {
                                  const today = new Date();
                                  const sameDay = today.toDateString() === date.toDateString();
                                  return sameDay ? 'Due Today' : `Due ${date.toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' })}`;
                                })()}
                              </div>
                              <ul className="cgs-items assignment-condensed">
                                {group.assignments.map(a => {
                                  const aid = a.assignmentId || a.id;
                                  const completed = effectiveCompleted(a);
                                  return (
                                    <li
                                      key={aid}
                                      className={`assignment-row condensed ${completed ? 'is-completed' : ''}`}
                                      onClick={(e) => {
                                        if (e.target.closest('.assignment-complete-btn')) return;
                                        handleAssignmentItemClick(a);
                                      }}
                                    >
                                      <span className="assignment-dot" aria-hidden="true" />
                                      <span className="assignment-row-title" title={a.title}>{a.title}</span>
                                      <div className="assignment-actions">
                                        <motion.button
                                          type="button"
                                          className={`assignment-complete-btn motion-btn ${completed ? 'completed' : ''}`}
                                          aria-label={completed ? `Mark ${a.title} incomplete` : `Mark ${a.title} complete`}
                                          aria-checked={completed}
                                          role="checkbox"
                                          title={completed ? `Mark ${a.title} incomplete` : `Mark ${a.title} complete`}
                                          whileTap={{ scale: 1.2 }}
                                          onClick={(e) => { e.stopPropagation(); handleToggleAssignmentComplete(a); }}
                                          style={completed ? {
                                            background: 'linear-gradient(135deg,#3b82f6 0%, #60a5fa 55%, #93c5fd 100%)',
                                            borderColor: '#3b82f6',
                                            boxShadow: '0 0 0 2px rgba(59,130,246,.35), 0 2px 4px rgba(0,0,0,.12)'
                                          } : undefined}
                                        >
                                          <motion.span
                                            className="acb-icon"
                                            initial={false}
                                            animate={completed ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }}
                                            transition={{ duration: 0.3, ease: 'easeOut' }}
                                            aria-hidden="true"
                                          >✓</motion.span>
                                        </motion.button>
                                        <AnimatePresence>
                                          {completed && (
                                            <motion.span
                                              key="completed-pill-day"
                                              className="assignment-completed-pill linked"
                                              title="Completed"
                                              initial={{ opacity: 0, x: 8 }}
                                              animate={{ opacity: 1, x: 0 }}
                                              exit={{ opacity: 0, x: 8 }}
                                              transition={{ duration: 0.25, ease: 'easeOut' }}
                                            >Completed</motion.span>
                                          )}
                                        </AnimatePresence>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Assignment Details Modal */}
      <CenteredModal
        isOpen={!!selectedAssignment}
        onClose={() => setSelectedAssignment(null)}
        title={selectedAssignment?.title || selectedAssignment?.name}
        onDelete={selectedAssignment ? async () => {
          await deleteAssignment(selectedAssignment.assignmentId || selectedAssignment.id);
          addToast('Assignment deleted successfully', 'success');
          setSelectedAssignment(null);
          loadAggregatedEvents();
        } : undefined}
      >
        {selectedAssignment && (
          <AssignmentDetails
            assignment={selectedAssignment}
            onClose={() => setSelectedAssignment(null)}
            onUpdate={(updatedAssignment) => {
              setSelectedAssignment(updatedAssignment);
            }}
            onSaved={() => {
              setSelectedAssignment(null);
              loadAggregatedEvents();
            }}
            onMarkComplete={async (assignment) => {
              await handleToggleAssignmentComplete(assignment);
              setSelectedAssignment(null);
            }}
            onEdit={() => {
              const assignmentId = selectedAssignment.assignmentId || selectedAssignment.id;
              if (assignmentId) {
                navigate(`/assignment/${assignmentId}`, { state: { returnTo: '/calendar' } });
              }
              setSelectedAssignment(null);
            }}
          />
        )}
      </CenteredModal>

      {/* Event Details Modal */}
      <CenteredModal
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        title={selectedEvent?.title || selectedEvent?.name}
        isRecurringEvent={selectedEvent && (selectedEvent.isRecurring || selectedEvent.recurrenceRule || selectedEvent.parentEventId)}
        onDelete={selectedEvent ? async () => {
          await deleteEvent(selectedEvent.eventId || selectedEvent.id);
          addToast('Event deleted successfully', 'success');
          setSelectedEvent(null);
          loadAggregatedEvents();
        } : undefined}
        onDeleteSeries={selectedEvent ? async () => {
          const parentId = selectedEvent.parentEventId || selectedEvent.eventId || selectedEvent.id;
          console.log('🗑️ [Calendar] Delete series clicked for event:', {
            selectedEvent,
            parentId,
            eventId: selectedEvent.eventId,
            id: selectedEvent.id,
            parentEventId: selectedEvent.parentEventId,
            isRecurring: selectedEvent.isRecurring,
            recurrenceRule: selectedEvent.recurrenceRule
          });
          try {
            await deleteEventSeries(parentId);
            addToast('Event series deleted successfully', 'success');
            setSelectedEvent(null);
            loadAggregatedEvents();
          } catch (error) {
            console.error('❌ [Calendar] Delete series error:', error);
            addToast('Failed to delete event series', 'error');
          }
        } : undefined}
      >
        {selectedEvent && (
          <EventDetails
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onUpdate={(updatedEvent) => {
              setSelectedEvent(updatedEvent);
            }}
            onSaved={() => {
              setSelectedEvent(null);
              loadAggregatedEvents();
            }}
            onEdit={() => {
              const eventId = selectedEvent.eventId || selectedEvent.id;
              if (eventId) {
                navigate(`/event/${eventId}`, { state: { returnTo: '/calendar' } });
              }
              setSelectedEvent(null);
            }}
          />
        )}
      </CenteredModal>

      {/* Create Event/Assignment Modal */}
      <AddToCalendarModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleModalCreated}
        prefill={{
          selectedDate: date.toISOString().split('T')[0],
          endeavorId: scopeEndeavorId || null
        }}
      />
    </div>
  );
}

CalendarComponent.refresh = () => {
  if (window.calendarRefresh) window.calendarRefresh();
};

export default CalendarComponent;



